import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import { Sticky } from "../Sticky/Sticky";
import { useScroll } from "../../hooks/use-scroll";
import { useRect } from "../../hooks/use-rect";
import { useWindowSize } from "react-use";
import { mapRange, clamp } from "../../utils/Maths";
import { useTranslation } from "react-i18next";

import { furnitureInEase } from "../../utils/Eases";

import furniture1 from "../../assets/images/collection/An-entire-collection-1.png";
import furniture2 from "../../assets/images/collection/An-entire-collection-2.png";
import furniture3 from "../../assets/images/collection/An-entire-collection-3.png";
import furniture4 from "../../assets/images/collection/An-entire-collection-4.png";
import furniture5 from "../../assets/images/collection/An-entire-collection-5.png";
import furniture6 from "../../assets/images/collection/An-entire-collection-6.png";

import s from "./Collection.module.scss";

const Collection = () => {
  const elementRef = useRef(null);
  const innerRef = useRef(null);
  const furnituresRef = useRef(null);

  const { t } = useTranslation();

  const [wrapperRectRef, wrapperRect] = useRect();
  const [elementRectRef, elementRect] = useRect();

  const { height: windowHeight } = useWindowSize();

  const [windowWidth, setWindowWidth] = useState();

  useScroll(({ scroll }) => {
    if (!elementRect || !elementRef.current) return;

    const start = wrapperRect.top - windowHeight * 0.5;
    const end = wrapperRect.top + wrapperRect.height - windowHeight;

    let progress = mapRange(start, end, scroll, 0, 1);
    progress = clamp(0, progress, 1);

    const x = windowWidth - (elementRect.width + windowWidth * 1.02) * progress;

    const xFurniture =
      windowWidth - (elementRect.width + windowWidth) * 0.6 * progress;

    gsap.to(elementRef.current, {
      x: x,
      ease: "none",
      duration: 0,
    });

    gsap.to(furnituresRef.current, {
      x: xFurniture,
      ease: "none",
      duration: 0,
    });
  });

  useEffect(() => {
    // const sentence = "An entire collection imagined by Philippe Starck";
    const sentence = t("collection");
    const letters = sentence.split("");

    letters.forEach((letter) => {
      const span = document.createElement("span");
      span.innerHTML = letter;

      if (letter === " ") {
        span.classList.add(s.space);
      }

      elementRef.current.appendChild(span);
    });

    gsap.fromTo(
      elementRef.current.children,
      {
        opacity: 0.14,
        rotateX: window.innerWidth < 1024 ? 0 : -90,
        z: -100,
      },
      {
        ease: "none",
        opacity: 0.2,
        rotateX: 0,
        z: 0,
        stagger: 0.1,
        scrollTrigger: {
          start: `${-window.innerHeight * 0.5} top`,
          end: `+=${window.innerHeight * 4}px`,
          scrub: true,
          trigger: innerRef.current,
        },
      }
    );

    const animationData = [
      {
        from: {
          rotateZ: -10,
        },
        to: {
          y: "10%",
          rotateZ: 0,
        },
      },
      {
        from: {
          rotateZ: 10,
          scaleX: -1,
        },
        to: {
          y: "-10%",
          rotateZ: 0,
          scaleX: -1,
        },
      },
      {
        from: {
          rotateZ: -10,
        },
        to: {
          y: "10%",
          rotateZ: 0,
        },
      },
      {
        from: {
          rotateZ: -10,
        },
        to: {
          y: "0%",
          rotateZ: 0,
        },
      },
      {
        from: {
          rotateZ: 10,
          scaleX: -1,
        },
        to: {
          y: "-10%",
          rotateZ: 0,
          scaleX: -1,
        },
      },
      {
        from: {
          rotateZ: -10,
        },
        to: {
          y: "10%",
          rotateZ: 0,
        },
      },
    ];

    Array.from(furnituresRef.current.children).forEach((child, index) => {
      const start = (320 / 6) * index;

      const offset = window.innerWidth < 1024 ? -10 : 0;

      gsap.fromTo(
        child,
        {
          y: "-50%",
          opacity: 0,
          rotateZ: animationData[index].from.rotateZ,
          scaleX: animationData[index].to.scaleX,
        },
        {
          y: animationData[index].to.y,
          rotateZ: animationData[index].to.rotateZ,
          scaleX: animationData[index].to.scaleX,
          opacity: 1,
          ease: furnitureInEase,

          scrollTrigger: {
            start: `${start + offset}% top`,
            end: `+=${window.innerHeight * 0.8}`,
            scrub: true,
            // markers: {},
            trigger: innerRef.current,
          },
        }
      );
    });

    // gsap.fromTo(
    //   furnituresRef.current.children,
    //   {
    //     opacity: 0,
    //     y: "-50%",
    //     rotateZ: -10,
    //   },
    //   {
    //     ease: "back.inOut(2)",
    //     rotateZ: 0,
    //     opacity: 1,
    //     y: "0%",
    //     duration: 0.05,
    //     // rotateX: 0,
    //     // z: 0,
    //     stagger: 0.06,
    //     scrollTrigger: {
    //       start: `top top`,
    //       end: `+=${window.innerHeight * 3}px`,
    //       scrub: true,
    //       markers: {},
    //       trigger: innerRef.current,
    //     },
    //   }
    // );

    furnituresRef.current.style.width = elementRef.current.offsetWidth + "px";

    const onResize = () => {
      setWindowWidth(
        Math.min(window.innerWidth, document.documentElement.offsetWidth)
      );
    };

    window.addEventListener("resize", onResize, false);
    onResize();

    return () => {
      window.removeEventListener("resize", onResize, false);
    };
  }, []);

  return (
    <div ref={wrapperRectRef} className={s.collection}>
      <div ref={innerRef} className={s.inner}>
        <h1
          ref={(node) => {
            elementRef.current = node;
            elementRectRef(node);
          }}
        ></h1>

        <div ref={furnituresRef} className={s.furnitures}>
          <img src={furniture1} alt="" />
          <img src={furniture2} alt="" />
          <img src={furniture3} alt="" />
          <img src={furniture4} alt="" />
          <img src={furniture5} alt="" />
          <img src={furniture6} alt="" />
        </div>
      </div>
    </div>
  );
};

export default Collection;
